import type { BankReconciliation, LedgerMovement, StatementLineToReview } from "@/api/types";

const DAY = 86_400_000;

/** The same window the API's candidates use. A match outside it is allowed, just not suggested. */
const WINDOW_DAYS = 30;

const paisa = (value: string) => Math.round(Number(value) * 100);

/**
 * The movement to suggest for each line: the same amount, nearest in date, within the window. Each
 * movement is offered once, oldest line first, so two lines for the same amount don't both claim it.
 *
 * Only a suggestion. Nothing is matched until someone accepts it, because two payments of one amount on
 * one day can be two different customers.
 */
export function suggestMatches(
  reconciliation: Pick<BankReconciliation, "onStatementOnly" | "inLedgerOnly">,
): Map<string, LedgerMovement> {
  const taken = new Set<string>();
  const suggested = new Map<string, LedgerMovement>();
  const daysApart = (line: StatementLineToReview, movement: LedgerMovement) =>
    Math.abs(Date.parse(line.date) - Date.parse(movement.date)) / DAY;

  for (const line of reconciliation.onStatementOnly) {
    const best = reconciliation.inLedgerOnly
      .filter(
        (movement) =>
          !taken.has(movement.journalLineId) &&
          paisa(movement.amount) === paisa(line.amount) &&
          daysApart(line, movement) <= WINDOW_DAYS,
      )
      .sort((a, b) => daysApart(line, a) - daysApart(line, b))[0];
    if (best) {
      taken.add(best.journalLineId);
      suggested.set(line.id, best);
    }
  }
  return suggested;
}
